// MP4 / MOV (ISO BMFF) metadata scrubbing, in-browser.
// Metadata boxes are renamed to 'free' and zero-filled instead of being cut out:
// byte offsets stay valid (stco/co64 reference absolute file positions), so the
// output plays everywhere while carrying no provenance data.
import { latin1, containsAny, indexOfBytes } from "./bytes";
import { AI_META_HINTS, C2PA_MARKERS } from "./imageMeta";

const ALL_HINTS = [...new Set([...AI_META_HINTS, ...C2PA_MARKERS])];
const CONTAINERS = new Set(["moov", "trak", "mdia", "minf", "stbl", "edts", "mvex"]);
const META_BOXES = new Set(["udta", "meta", "uuid", "©cpy", "skip"]);

function readBoxes(u8, start, end) {
  const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
  const boxes = [];
  let pos = start;
  while (pos + 8 <= end) {
    let size = dv.getUint32(pos);
    const type = latin1(u8, pos + 4, pos + 8);
    let contentStart = pos + 8;
    if (size === 1) {
      const hi = dv.getUint32(pos + 8);
      const lo = dv.getUint32(pos + 12);
      size = hi * 4294967296 + lo;
      contentStart = pos + 16;
    } else if (size === 0) {
      size = end - pos;
    }
    if (size < 8 || pos + size > end) break;
    boxes.push({ type, start: pos, size, contentStart, contentEnd: pos + size });
    pos += size;
  }
  return boxes;
}

export function isVideoFormat(name, u8) {
  const ext = (name.split(".").pop() || "").toLowerCase();
  if (["mp4", "m4v", "mov", "m4a"].includes(ext)) return true;
  return u8 && u8.length > 12 && latin1(u8, 4, 8) === "ftyp";
}

function collectTargets(u8, start, end, path, out) {
  for (const box of readBoxes(u8, start, end)) {
    const p = `${path}/${box.type}`;
    if (META_BOXES.has(box.type)) {
      out.push({ ...box, path: p });
      continue;
    }
    if (CONTAINERS.has(box.type)) collectTargets(u8, box.contentStart, box.contentEnd, p, out);
  }
}

export function inspectVideo(u8) {
  const findings = [];
  const targets = [];
  collectTargets(u8, 0, u8.length, "", targets);
  for (const box of targets) {
    const hits = containsAny(u8.subarray(box.contentStart, box.contentEnd), ALL_HINTS);
    const label = `${box.path.replace(/^\//, "")} (${box.size} bytes)`;
    findings.push(hits.length ? `${label}: ${hits.slice(0, 6).join(", ")}` : `metadata box ${label}`);
  }
  if (indexOfBytes(u8, "<x:xmpmeta") !== -1) findings.push("XMP packet present");
  const whole = containsAny(u8, C2PA_MARKERS);
  const hasC2pa = whole.length > 0;
  if (hasC2pa) findings.push(`byte-scan C2PA markers: ${whole.slice(0, 6).join(", ")}`);
  return { hasC2pa, hasAi: hasC2pa || findings.length > 0, findings };
}

export function cleanVideo(u8) {
  const out = new Uint8Array(u8);
  const actions = [];
  const targets = [];
  collectTargets(out, 0, out.length, "", targets);
  for (const box of targets) {
    out[box.start + 4] = 0x66; // f
    out[box.start + 5] = 0x72; // r
    out[box.start + 6] = 0x65; // e
    out[box.start + 7] = 0x65; // e
    out.fill(0, box.contentStart, box.contentEnd);
    actions.push(`neutralise ${box.path.replace(/^\//, "")} box (${box.size} bytes)`);
  }
  if (!actions.length) actions.push("no video metadata boxes found (already clean)");
  return { bytes: out, actions };
}
